import AES from 'crypto-js/aes';
import UTF8 from 'crypto-js/enc-utf8';

import { LanguageCode } from '~layouts/Internationalization/Internationalization';
import type { SidebarData } from '~layouts/Sidebar/SidebarTypes';
import { type Tenant } from '~pages/Public/LoginPage/LoginRequests';
import type { AppImplementationConfig } from './useImplementation';

export interface UserStorage {
  email: string;
  first_name: string;
  last_name: string;
  permissions: string[];
  pk: number;
  sidebarData: SidebarData[];
  tac: unknown;
  tokens: {
    access: string;
    refresh: string;
  };
  username: string;
  is_superuser: boolean;
  url: string;
  availableTenants: Tenant[];
  tenantSelected: Tenant;
  userCredentials: {
    password: string;
    username: string;
  };
}

type Store = UserStorage | AppImplementationConfig;

const useLocalStorage = () => {
  const appName = 'karya_platform';
  const implementation = 'karya_implementation';

  const encryptUserData = (data: Partial<Store>) => {
    const dataEncrypted = AES.encrypt(
      JSON.stringify(data),
      import.meta.env.VITE_ENCRYPTION_KEY
    ).toString();
    return dataEncrypted;
  };
  const decryptUserData = (data: string): Partial<Store> => {
    let userDataDecrypted = {};
    if (data !== null) {
      const bytes = AES.decrypt(data, import.meta.env.VITE_ENCRYPTION_KEY);
      userDataDecrypted = JSON.parse(bytes.toString(UTF8)) as string;
    }
    return userDataDecrypted;
  };

  const userData = decryptUserData(
    localStorage.getItem(appName)!
  ) as UserStorage;
  const userPermissions = userData?.permissions ?? [];
  const userAccess = userData?.tokens?.access;

  const updateUserData = (newUserData = {}) => {
    const prevUserData = decryptUserData(localStorage.getItem(appName)!) || {};
    const updatedUserData = { ...prevUserData, ...newUserData };
    localStorage.setItem(appName, encryptUserData(updatedUserData));
  };

  const recaptcha = localStorage.getItem('recaptcha') ?? '';
  const setRecaptcha = (token: string) => {
    localStorage.setItem('recaptcha', JSON.stringify(token));
  };
  const removeRecaptcha = () => localStorage.removeItem('recaptcha');

  const appImplementation = decryptUserData(
    localStorage.getItem(implementation)!
  ) as AppImplementationConfig;
  const updateAppImplementation = (newImplementationData = {}) => {
    localStorage.setItem(
      implementation,
      encryptUserData(newImplementationData)
    );
  };

  const setLanguage = (language: string) => {
    localStorage.setItem('language', language);
  };

  const language: LanguageCode =
    (localStorage.getItem('language') as LanguageCode) ??
    import.meta.env.VITE_DEFAULT_LANGUAGE;

  return {
    appName,
    userData,
    userAccess,
    userPermissions,
    recaptcha,
    appImplementation,
    language,
    setRecaptcha,
    removeRecaptcha,
    updateUserData,
    updateAppImplementation,
    setLanguage
  };
};

export default useLocalStorage;
